import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common/Common";

import Home from "@/pages/marketing/Home";
import Platform from "@/pages/marketing/Platform";
import Business from "@/pages/marketing/Business";
import Intelligence from "@/pages/marketing/Intelligence";
import MarketplacePublic from "@/pages/marketing/MarketplacePublic";
import Pricing from "@/pages/marketing/Pricing";
import GenericPage from "@/pages/marketing/GenericPage";
import { Login, Register, AuthCallback } from "@/pages/auth/Auth";
import Blog from "@/pages/marketing/Blog";
import BlogPost from "@/pages/marketing/BlogPost";
import { Analytics } from "@/components/Analytics";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ChoosePath from "@/pages/onboarding/ChoosePath";
import OnboardingFlow from "@/pages/onboarding/OnboardingFlow";
import BusinessOnboarding from "@/pages/onboarding/BusinessOnboarding";
import HomeownerDashboard from "@/pages/app/HomeownerDashboard";
import BusinessOverview from "@/pages/business/Overview";
import {
  BizLeads, BizClients, BizProjects, BizEstimation, BizProcurement, BizMaterials,
  BizVendors, BizWorkforce, BizBOQ, BizQuality, BizDocuments, BizFinance, BizReports,
  BizCopilot, BizTeam, BizSettings, BizBilling,
} from "@/pages/business/pages";
import ProjectWorkspace from "@/pages/app/ProjectWorkspace";
import Copilot from "@/pages/app/Copilot";
import AppStudio from "@/pages/app/AppStudio";
import AdminPanel from "@/pages/app/AdminPanel";
import ClientPortal from "@/pages/client/ClientPortal";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen"><Spinner label="Loading Sitepar..." /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppEntry() {
  const { user } = useAuth();
  if (user && user.email_verified === false) return <Navigate to="/verify-email" replace />;
  if (!user?.account_type) return <Navigate to="/onboarding" replace />;
  return <Navigate to={user.account_type === "business" ? "/business/app" : "/app/home"} replace />;
}

function GuestOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen"><Spinner /></div>;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {/* Public marketing */}
      <Route path="/" element={<Home />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/platform" element={<Platform />} />
      <Route path="/business" element={<Business />} />
      <Route path="/intelligence" element={<Intelligence />} />
      <Route path="/market" element={<MarketplacePublic />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/solutions" element={<GenericPage slug="solutions" />} />
      <Route path="/industries" element={<GenericPage slug="industries" />} />
      <Route path="/resources" element={<GenericPage slug="resources" />} />
      <Route path="/company" element={<GenericPage slug="company" />} />
      <Route path="/studio" element={<GenericPage slug="studio" />} />
      <Route path="/home-product" element={<GenericPage slug="home-product" />} />

      {/* Auth */}
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/signup" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/verify-email" element={<Protected><VerifyEmail /></Protected>} />

      {/* Onboarding */}
      <Route path="/onboarding" element={<Protected><ChoosePath /></Protected>} />
      <Route path="/onboarding/home" element={<Protected><OnboardingFlow /></Protected>} />
      <Route path="/onboarding/profile" element={<Protected><OnboardingFlow /></Protected>} />
      <Route path="/onboarding/property" element={<Protected><OnboardingFlow /></Protected>} />
      <Route path="/onboarding/documents" element={<Protected><OnboardingFlow /></Protected>} />
      <Route path="/onboarding/requirements" element={<Protected><OnboardingFlow /></Protected>} />
      <Route path="/onboarding/review" element={<Protected><OnboardingFlow /></Protected>} />
      <Route path="/onboarding/success" element={<Protected><OnboardingFlow /></Protected>} />
      <Route path="/onboarding/business" element={<Protected><BusinessOnboarding /></Protected>} />

      {/* App */}
      <Route path="/app" element={<Protected><AppEntry /></Protected>} />
      <Route path="/app/home" element={<Protected><HomeownerDashboard /></Protected>} />

      {/* B2B Business Operating System */}
      <Route path="/business/app" element={<Protected><BusinessOverview /></Protected>} />
      <Route path="/business/leads" element={<Protected><BizLeads /></Protected>} />
      <Route path="/business/clients" element={<Protected><BizClients /></Protected>} />
      <Route path="/business/projects" element={<Protected><BizProjects /></Protected>} />
      <Route path="/business/estimation" element={<Protected><BizEstimation /></Protected>} />
      <Route path="/business/boq" element={<Protected><BizBOQ /></Protected>} />
      <Route path="/business/procurement" element={<Protected><BizProcurement /></Protected>} />
      <Route path="/business/materials" element={<Protected><BizMaterials /></Protected>} />
      <Route path="/business/vendors" element={<Protected><BizVendors /></Protected>} />
      <Route path="/business/workforce" element={<Protected><BizWorkforce /></Protected>} />
      <Route path="/business/quality" element={<Protected><BizQuality /></Protected>} />
      <Route path="/business/documents" element={<Protected><BizDocuments /></Protected>} />
      <Route path="/business/finance" element={<Protected><BizFinance /></Protected>} />
      <Route path="/business/reports" element={<Protected><BizReports /></Protected>} />
      <Route path="/business/ai" element={<Protected><BizCopilot /></Protected>} />
      <Route path="/business/team" element={<Protected><BizTeam /></Protected>} />
      <Route path="/business/settings" element={<Protected><BizSettings /></Protected>} />
      <Route path="/business/billing" element={<Protected><BizBilling /></Protected>} />

      {/* Legacy business routes → redirect to new B2B OS */}
      <Route path="/app/business" element={<Navigate to="/business/app" replace />} />
      <Route path="/app/business/leads" element={<Navigate to="/business/leads" replace />} />
      <Route path="/app/business/projects" element={<Navigate to="/business/projects" replace />} />
      <Route path="/app/project/:projectId" element={<Protected><ProjectWorkspace /></Protected>} />
      <Route path="/app/copilot" element={<Protected><Copilot /></Protected>} />
      <Route path="/app/studio" element={<Protected><AppStudio /></Protected>} />
      <Route path="/app/admin" element={<Protected><AdminPanel /></Protected>} />

      {/* Client portal */}
      <Route path="/client/:projectId" element={<Protected><ClientPortal /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TitleManager />
        <Analytics />
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

const SITE = "Sitepar";
const TITLES = [
  [/^\/$/, "Sitepar · Construction Operating System"],
  [/^\/platform/, "Platform"],
  [/^\/business$/, "For Construction Businesses"],
  [/^\/intelligence/, "Sitepar Intelligence"],
  [/^\/market/, "Marketplace"],
  [/^\/pricing/, "Pricing"],
  [/^\/login/, "Sign in"],
  [/^\/register/, "Create your account"],
  [/^\/verify-email/, "Verify your email"],
  [/^\/onboarding\/business/, "Set up your business"],
  [/^\/onboarding/, "Get started"],
  [/^\/business\/app/, "Command Center"],
  [/^\/business\/leads/, "Leads"],
  [/^\/business\/clients/, "Clients"],
  [/^\/business\/projects/, "Projects"],
  [/^\/business\/estimation/, "Estimation"],
  [/^\/business\/boq/, "BOQ"],
  [/^\/business\/procurement/, "Procurement"],
  [/^\/business\/materials/, "Materials"],
  [/^\/business\/vendors/, "Vendors"],
  [/^\/business\/workforce/, "Workforce"],
  [/^\/business\/quality/, "Quality"],
  [/^\/business\/documents/, "Documents"],
  [/^\/business\/finance/, "Finance"],
  [/^\/business\/reports/, "Reports"],
  [/^\/business\/ai/, "AI Copilot"],
  [/^\/business\/team/, "Team"],
  [/^\/business\/settings/, "Settings"],
  [/^\/business\/billing/, "Billing"],
  [/^\/app\/home/, "My Project"],
  [/^\/app\/project/, "Project Workspace"],
  [/^\/app\/copilot/, "AI Copilot"],
  [/^\/app\/studio/, "Design Studio"],
  [/^\/app\/admin/, "Platform Admin"],
  [/^\/client\//, "Client Portal"],
];

function TitleManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    const match = TITLES.find(([re]) => re.test(pathname));
    const label = match ? match[1] : null;
    document.title = !label || label.startsWith(SITE) ? (label || `${SITE} · Construction Operating System`) : `${label} · ${SITE}`;
  }, [pathname]);
  return null;
}
